import { i18n } from '../i18n.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { CopyText } from '../ui/copy-text.js';
import { Platform } from '../ui/platform.js';
/**
 * Shared social share button factory.
 * Used by the in-game share modal and the public storybook viewer.
 *
 * attachShareButtons(container, {
 *   url, title, text, description, hashtags, locale,
 *   onCopy, onShared, t (i18n function),
 *   discord: { invite, label, copied, copyFailed, onOpened } (beside Share on a phone, before Copy link on a desk)
 * })
 *
 * `text` is the line a caller that is not a storybook writes itself (a Moon
 * Exchange listing): it goes out wherever a storybook's match line would,
 * the Reddit title included.
 *
 * A phone or tablet with Web Share renders one automatic picture-first Share
 * action. Everything else — every desk, even one whose browser has a share
 * menu — renders the networks and Copy link. Never both on one device.
 */
(function () {
    if (typeof window === 'undefined') return;
    window.MoonfallStorybookShare = window.MoonfallStorybookShare || {};

    const MAX_NATIVE_SHARE_IMAGE_BYTES = 5 * 1024 * 1024;
    const NATIVE_SHARE_IMAGE_TIMEOUT_MS = 8_000;
    const SHARE_FORMATS = Object.freeze({
        LINK_CARD: 'link_card',
        IMAGE_ATTACHMENT: 'image_attachment',
        IMAGE_DOWNLOAD: 'image_download',
    });
    let warmedShareImage = null;

    function defaultT(key, vars) {
        const fn = i18n && typeof i18n.t === 'function' ? i18n.t : null;
        if (!fn) return key;
        return fn(key, vars);
    }

    function translated(t, key, fallback, vars = {}) {
        try {
            const value = t(key, vars);
            return typeof value === 'string' && value && value !== key
                ? value
                : fallback;
        } catch (_) {
            return fallback;
        }
    }

    function storybookUrlApi() {
        return window.MoonfallStorybookUrl || null;
    }

    function mediaKind(ctx) {
        if (['manga', 'cover', 'story'].includes(ctx.imageKind)) return ctx.imageKind;
        return ctx.imageUrl ? 'manga' : 'story';
    }

    /**
     * @param {object} ctx
     * @param {string} channel
     * @param {string} [shareFormat]
     */
    function attributedUrl(ctx, channel, shareFormat = SHARE_FORMATS.LINK_CARD) {
        const helper = storybookUrlApi();
        return helper?.withShareAttribution
            ? helper.withShareAttribution(ctx.url, channel, {
                mediaKind: mediaKind(ctx),
                shareFormat,
                /* Same reason `analyticsPrefix` exists: a listing shared through
                   this bar must not be filed under the storybook's campaign. */
                campaign: ctx.utmCampaign,
                content: ctx.utmContent,
            })
            : ctx.url;
    }

    /* A listing has no match: built from the storybook's two sides, its line
       read "My latest Moonfall match recap:  vs ." on every network. */
    function callerText(ctx) {
        return typeof ctx.text === 'string' && ctx.text.trim() ? ctx.text : null;
    }

    function buildLeadText(ctx) {
        const own = callerText(ctx);
        if (own) return own;
        const t = ctx.t || defaultT;
        const a = ctx.ownerCharacter || '';
        const b = ctx.opponentCharacter || '';
        const isManga = mediaKind(ctx) === 'manga';
        const key = isManga ? 'share.text.manga' : 'share.text.match';
        const fallback = isManga
            ? 'My latest Moonfall match, inked as a manga. See how it ended.'
            : `My latest Moonfall match recap: ${a} vs ${b}.`;
        return translated(t, key, fallback, { a, b });
    }

    function buildRedditTitle(ctx) {
        const own = callerText(ctx);
        if (own) return own;
        const t = ctx.t || defaultT;
        const a = ctx.ownerCharacter || '';
        const b = ctx.opponentCharacter || '';
        return translated(
            t,
            'share.text.reddit.title',
            `My Moonfall storybook: ${a} vs ${b}`,
            { a, b },
        );
    }

    function buildAttachmentText(ctx) {
        return `${buildLeadText(ctx)}\n\n${ctx.url}`;
    }

    /**
     * @param {string} eventName
     * @param {object} ctx
     * @param {string} channel
     * @param {string} [shareFormat] one of SHARE_FORMATS — declared, because
     *   the default alone narrows the parameter to that single literal and
     *   every caller passing a chosen format is then a type error.
     */
    function captureShare(
        eventName,
        ctx,
        channel,
        shareFormat = SHARE_FORMATS.LINK_CARD,
    ) {
        const capture = MoonfallAnalytics?.capture;
        if (typeof capture !== 'function') return;
        /* ADDITIVE. This bar is no longer storybook-only -- a Moon Exchange
           listing shares through exactly the same controls -- and a share of a
           listing filed under `storybook_share_*` silently corrupts the funnel
           both surfaces are measured on. A caller that sets no prefix gets the
           original event names, unchanged. */
        const named = ctx.analyticsPrefix
            ? eventName.replace(/^storybook_/, `${ctx.analyticsPrefix}_`)
            : eventName;
        try {
            capture(named, {
                channel,
                share_format: shareFormat,
                media_kind: mediaKind(ctx),
                language: ctx.language
                    || storybookUrlApi()?.resolveLanguage?.()
                    || 'en',
                surface: ctx.surface || 'unknown',
            });
        } catch (_) {
            // Analytics is observational and must never consume a share click.
        }
    }

    async function preloadShareImage(imageUrl) {
        if (!imageUrl || typeof fetch !== 'function' || typeof File !== 'function') return null;

        const controller = typeof AbortController === 'function'
            ? new AbortController()
            : null;
        let request;
        try {
            request = Promise.resolve(fetch(imageUrl, {
                credentials: 'omit',
                mode: 'cors',
                cache: 'force-cache',
                ...(controller ? { signal: controller.signal } : {}),
            })).catch(() => null);
        } catch (_) {
            return null;
        }
        let timer = null;
        const timeout = new Promise((resolve) => {
            timer = setTimeout(() => {
                controller?.abort();
                resolve(null);
            }, NATIVE_SHARE_IMAGE_TIMEOUT_MS);
        });

        try {
            const response = await Promise.race([request, timeout]);
            if (!response?.ok) return null;

            const declaredSize = Number(response.headers?.get?.('content-length'));
            if (Number.isFinite(declaredSize) && declaredSize > MAX_NATIVE_SHARE_IMAGE_BYTES) {
                return null;
            }

            const blob = await Promise.race([
                Promise.resolve(response.blob()).catch(() => null),
                timeout,
            ]);
            if (
                !blob?.size
                || blob.size > MAX_NATIVE_SHARE_IMAGE_BYTES
                || String(blob.type || '').toLowerCase() !== 'image/jpeg'
            ) return null;

            return new File([blob], 'moonfall-storybook.jpg', {
                type: 'image/jpeg',
                lastModified: Date.now(),
            });
        } catch (_) {
            return null;
        } finally {
            if (timer !== null) clearTimeout(timer);
        }
    }

    function supportsNativeImageShare() {
        if (
            typeof navigator.share !== 'function'
            || typeof navigator.canShare !== 'function'
            || typeof fetch !== 'function'
            || typeof File !== 'function'
        ) return false;

        try {
            const probe = new File(['moonfall'], 'moonfall-storybook.jpg', {
                type: 'image/jpeg',
            });
            return navigator.canShare({ files: [probe] });
        } catch (_) {
            return false;
        }
    }

    function warmShareImage(imageUrl) {
        if (!imageUrl) return Promise.resolve(null);
        if (warmedShareImage?.url === imageUrl) return warmedShareImage.promise;

        const entry = { url: imageUrl, promise: null };
        entry.promise = preloadShareImage(imageUrl).then((file) => {
            if (!file && warmedShareImage === entry) warmedShareImage = null;
            return file;
        });
        warmedShareImage = entry;
        return entry.promise;
    }

    function prepareShareImage(imageUrl) {
        return supportsNativeImageShare()
            ? warmShareImage(imageUrl)
            : Promise.resolve(null);
    }

    function shareNatively(button, payload, ctx, shareFormat) {
        if (!button || button.disabled) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        captureShare('storybook_share_started', ctx, 'native', shareFormat);

        let handoff;
        try {
            // Keep this invocation synchronous with the click. Safari's
            // transient user activation must not be spent awaiting other work.
            handoff = navigator.share(payload);
        } catch (_) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            return;
        }

        Promise.resolve(handoff).then(() => {
            captureShare('storybook_share_succeeded', ctx, 'native', shareFormat);
            if (typeof ctx.onShared === 'function') ctx.onShared('native');
        }).catch((error) => {
            // AbortError is the reader closing the sheet — nothing to say.
            // Anything else is the browser refusing to open one, which WebKit
            // does for the rest of the page's life once a sheet was torn down
            // by an app switch without reporting back ("share() is already in
            // progress"). This is the row's ONE action where Web Share exists,
            // so a silent refusal was a dead button (iOS Brave, 2026-09-15).
            if (error?.name === 'AbortError' || typeof ctx.onCopy !== 'function') return;
            const t = ctx.t || defaultT;
            ctx.onCopy(
                translated(t, 'share.modal.nativeUnavailable', 'Sharing is unavailable right now. Copy the link instead.'),
                { ok: false, nativeShareFailed: true },
            );
        }).finally(() => {
            button.disabled = false;
            button.removeAttribute('aria-busy');
        });
    }

    /* The shared ladder (ui/copy-text.js). The local fallback this replaces
       parked a textarea at left:-9999px and called a bare .select() — the
       exact shape iOS refuses to copy from, which the ladder was written to
       retire everywhere else. The sheet rung stays off: this runs either from
       the row's own Copy button (only rendered where Web Share is absent) or
       after a blocked intent popup, and neither may surprise-open a sheet. */
    async function copyText(text) {
        const outcome = await CopyText.copy(text, { allowShareFallback: false });
        return outcome.ok;
    }

    // Inline SVG icons. 24x24 viewBox, fill="currentColor" so they inherit
    // button color. Sourced from each brand's public guideline (single-color form).
    const ICONS = {
        twitter: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.25 2.25h6.866l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/></svg>',
        facebook: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M22 12.06C22 6.51 17.52 2 12 2S2 6.51 2 12.06c0 5.02 3.66 9.18 8.44 9.93v-7.02H7.9v-2.9h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.77l-.44 2.9h-2.33V22c4.78-.75 8.43-4.91 8.43-9.94Z"/></svg>',
        reddit: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M22 11.92c0-1.21-.98-2.19-2.19-2.19-.59 0-1.13.24-1.52.62-1.49-1.07-3.55-1.77-5.85-1.85l1.19-3.74 3.18.75c.04.81.71 1.45 1.53 1.45.85 0 1.54-.69 1.54-1.54s-.69-1.54-1.54-1.54c-.6 0-1.12.35-1.37.86l-3.54-.84a.34.34 0 0 0-.4.23l-1.32 4.15c-2.33.06-4.42.76-5.94 1.84-.39-.38-.93-.62-1.52-.62A2.19 2.19 0 0 0 2 11.92c0 .82.45 1.53 1.11 1.91-.05.24-.07.49-.07.74 0 3.05 3.57 5.52 7.97 5.52 4.4 0 7.97-2.47 7.97-5.52 0-.25-.03-.5-.07-.74.65-.38 1.09-1.09 1.09-1.91Zm-14 1.34c0-.85.69-1.54 1.54-1.54s1.54.69 1.54 1.54-.69 1.54-1.54 1.54-1.54-.69-1.54-1.54Zm8.79 4.06c-1.16.95-2.97 1.41-4.79 1.41-1.83 0-3.64-.46-4.79-1.41a.4.4 0 0 1 .51-.62c.95.78 2.59 1.17 4.28 1.17 1.69 0 3.33-.39 4.28-1.18a.4.4 0 1 1 .51.63Zm-.34-2.52c-.85 0-1.54-.69-1.54-1.54s.69-1.54 1.54-1.54 1.54.69 1.54 1.54-.69 1.54-1.54 1.54Z"/></svg>',
        /* Simple Icons (CC0-1.0), simple-icons@16.32.0. */
        whatsapp: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>',
        telegram: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>',
        bluesky: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M5.202 2.857C7.954 4.922 10.913 9.11 12 11.358c1.087-2.247 4.046-6.436 6.798-8.501C20.783 1.366 24 .213 24 3.883c0 .732-.42 6.156-.667 7.037-.856 3.061-3.978 3.842-6.755 3.37 4.854.826 6.089 3.562 3.422 6.299-5.065 5.196-7.28-1.304-7.847-2.97-.104-.305-.152-.448-.153-.327 0-.121-.05.022-.153.327-.568 1.666-2.782 8.166-7.847 2.97-2.667-2.737-1.432-5.473 3.422-6.3-2.777.473-5.899-.308-6.755-3.369C.42 10.04 0 4.615 0 3.883c0-3.67 3.217-2.517 5.202-1.026"/></svg>',
        threads: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M18.263 11.097c-.03-3.486-1.92-5.586-5.111-5.586-2.13 0-3.922.963-4.863 2.499l2.062 1.438c.535-.843 1.272-1.543 2.628-1.543 1.528 0 2.318.85 2.544 2.431a15 15 0 0 0-2.236-.173c-4.125 0-6.068 1.867-6.068 4.336s1.943 3.99 4.804 3.99c3.139 0 5.013-2.115 5.781-4.735.798.361 1.348 1.204 1.348 2.47 0 3.387-3.907 5.232-7.22 5.232-4.885 0-8.077-3.207-8.077-8.424 0-6.392 4.223-10.487 9.9-10.487 3.808 0 5.69 1.671 6.97 3.914l2.108-1.475C21.44 2.078 18.331 0 13.663 0 6.227 0 1.168 5.277 1.168 12.934c0 7 4.953 11.066 10.856 11.066 4.878 0 9.809-2.846 9.809-7.716 0-2.545-1.46-4.231-3.569-5.187m-6.33 4.855c-1.077 0-2.026-.512-2.026-1.453 0-1.483 1.822-1.934 3.606-1.934.678 0 1.34.045 1.927.173-.422 1.927-1.671 3.215-3.508 3.214Z"/></svg>',
        discord: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/></svg>',
        copy: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>',
        native: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>',
    };

    function buildIconNode(iconKey) {
        // Parse SVG via DOMParser so we never touch innerHTML with markup.
        const svg = ICONS[iconKey];
        if (!svg) return null;
        try {
            const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
            const node = doc.documentElement;
            // DOMParser places the parsed SVG in a different namespace document;
            // adoptNode brings it into the live DOM cleanly.
            return document.adoptNode(node);
        } catch (_) {
            return null;
        }
    }

    function dressControl(control, className, label, iconKey) {
        control.className = `storybook-share-button ${className}`;
        control.setAttribute('aria-label', label);
        control.setAttribute('title', label);

        const iconWrap = document.createElement('span');
        iconWrap.className = 'storybook-share-icon';
        iconWrap.setAttribute('aria-hidden', 'true');
        const iconNode = buildIconNode(iconKey);
        if (iconNode) iconWrap.appendChild(iconNode);
        control.appendChild(iconWrap);

        const sr = document.createElement('span');
        sr.className = 'storybook-share-srlabel';
        sr.textContent = label;
        control.appendChild(sr);
        return control;
    }

    function makeButton(className, label, iconKey, onClick) {
        const btn = document.createElement('button');
        btn.type = 'button';
        dressControl(btn, className, label, iconKey);
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            onClick(btn);
        });
        return btn;
    }

    const DISCORD_INVITE = /^https:\/\/discord\.gg\/[A-Za-z0-9]+$/;

    /*
     * A DISCORD CHANNEL, FOR A CALLER THAT NAMES ONE (the Moon Exchange's
     * #trades; the storybook names none, so it never shows).
     *
     * Discord takes no pre-filled message from a website, so the button does
     * the next best thing in one tap: it copies the attributed link and opens
     * the channel's own invite. It is a real link, so the new tab opens from
     * the tap itself, and the copy starts before the tab can take the focus.
     * What the caller is told carries `ok`, so it can say "copied" only once
     * that is true.
     */
    function makeDiscordLink(ctx, discord) {
        const t = ctx.t || defaultT;
        const link = dressControl(document.createElement('a'), 'storybook-share-discord', discord.label, 'discord');
        link.href = discord.invite;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.addEventListener('click', () => {
            captureShare('storybook_share_started', ctx, 'discord', SHARE_FORMATS.LINK_CARD);
            try { discord.onOpened?.(); } catch (_) { /* counting never blocks the door */ }
            copyText(attributedUrl(ctx, 'discord')).then((ok) => {
                if (typeof ctx.onCopy === 'function') ctx.onCopy(
                    ok
                        ? discord.copied
                        : (discord.copyFailed || translated(t, 'share.modal.copyFailed', 'Could not copy share link')),
                    { ok, channel: 'discord' },
                );
                if (!ok) return;
                captureShare('storybook_share_succeeded', ctx, 'discord', SHARE_FORMATS.LINK_CARD);
                if (typeof ctx.onShared === 'function') ctx.onShared('discord');
                flashOk(link);
            });
        });
        return link;
    }

    function openIntent(url, ctx) {
        let opened = null;
        try {
            // Opening the destination with the `noopener` feature can return
            // null even when the browser did open it, making popup detection
            // impossible. Open a same-origin inert page first, sever its
            // opener synchronously, then navigate only after that check.
            opened = window.open('about:blank', '_blank');
        } catch (_) { opened = null; }

        if (opened) {
            try {
                opened.opener = null;
                if (opened.opener !== null) throw new Error('share_opener_not_detached');
                if (opened.location && typeof opened.location.replace === 'function') {
                    opened.location.replace(url);
                } else {
                    opened.location = url;
                }
                return true;
            } catch (_) {
                try { opened.close?.(); } catch (_) { /* already unavailable */ }
            }
        }

        // Popup blocked. Never navigate the host page away — the user would
        // lose their place on the storybook viewer or share modal. Instead,
        // copy the intent URL so they can paste it into a new tab, and emit
        // a toast so the failure is visible.
        const t = (ctx && ctx.t) || defaultT;
        const fallbackMsg = translated(
            t,
            'share.modal.popupBlocked',
            'Pop-ups blocked. Link copied; paste it in a new tab.',
        );
        copyText(url).then((ok) => {
            if (ctx && typeof ctx.onCopy === 'function') {
                ctx.onCopy(ok ? fallbackMsg : translated(
                    t,
                    'share.modal.copyFailed',
                    'Could not open share link',
                ), { ok, blockedByPopup: true });
            }
        });
        return false;
    }

    /*
     * ONE WAY TO SHARE ON EACH KIND OF DEVICE, never two competing.
     *
     * A phone or tablet has its own share menu with every app on it, so it
     * gets that single button. A desk gets the networks and Copy link, even
     * where its browser has a menu too (Safari and Chrome on a Mac do). The
     * owner's call: "only show the copy button and the socials buttons on
     * desktop, otherwise we create competing ways and decision fatigue".
     * iPadOS Safari calls itself a Mac; Platform's UA check counts its touch
     * points, so it stays a tablet.
     */
    function sharesThroughSheet() {
        if (typeof navigator.share !== 'function') return false;
        try {
            return Platform.isMobileUA() || Platform.isTouchPrimary();
        } catch (_) {
            return false;
        }
    }

    function attachShareButtons(container, ctx) {
        if (!container) return;
        while (container.firstChild) container.removeChild(container.firstChild);

        const t = ctx.t || defaultT;
        const url = ctx.url;
        if (!url) return;
        const nativeShareAvailable = sharesThroughSheet();

        function handleIntent(
            channel,
            intentUrl,
            shareFormat = SHARE_FORMATS.LINK_CARD,
        ) {
            captureShare('storybook_share_started', ctx, channel, shareFormat);
            const opened = openIntent(intentUrl, ctx);
            if (opened) {
                captureShare(
                    'storybook_share_intent_opened',
                    ctx,
                    channel,
                    shareFormat,
                );
                if (typeof ctx.onShared === 'function') ctx.onShared(channel);
            }
        }

        /* The caller's one Discord channel, when it names a valid one (the
           Moon Exchange's #trades); the storybook bar names none. */
        function appendDiscord() {
            const discord = ctx.discord;
            if (discord && DISCORD_INVITE.test(String(discord.invite || '')) && discord.label && discord.copied) {
                container.appendChild(makeDiscordLink(ctx, discord));
            }
        }

        // One visible Share action chooses the strongest payload already ready
        // at click time. A supported JPEG is attached with the attributed link
        // in its caption and no separate URL member. Until then, the same button
        // opens an immediate rich-card link without waiting or losing Safari's
        // transient user activation.
        if (nativeShareAvailable) {
            const linkUrl = attributedUrl(ctx, 'native', SHARE_FORMATS.LINK_CARD);
            const linkContext = { ...ctx, url: linkUrl };
            const linkPayload = {
                title: ctx.title || buildRedditTitle(linkContext),
                text: buildLeadText(linkContext),
                url: linkUrl,
            };
            let attachmentPayload = null;
            const nativeButton = makeButton('storybook-share-native', translated(t, 'share.modal.nativeLink', 'Share'), 'native', (button) => {
                const payload = attachmentPayload || linkPayload;
                shareNatively(
                    button,
                    payload,
                    ctx,
                    attachmentPayload
                        ? SHARE_FORMATS.IMAGE_ATTACHMENT
                        : SHARE_FORMATS.LINK_CARD,
                );
            });
            container.appendChild(nativeButton);
            // A phone's sheet lists Discord only if its app is installed, so a
            // caller's Discord channel keeps its own button here too (owner,
            // 28 Oct 2026: "I do not see the discord option ... on mobile").
            appendDiscord();

            if (ctx.imageUrl) {
                prepareShareImage(ctx.imageUrl).then((file) => {
                    if (!file || !nativeButton.isConnected || !container.contains(nativeButton)) return;

                    const imageUrl = attributedUrl(
                        ctx,
                        'native',
                        SHARE_FORMATS.IMAGE_ATTACHMENT,
                    );
                    const imageContext = { ...ctx, url: imageUrl };
                    const candidate = {
                        title: ctx.title || buildRedditTitle(imageContext),
                        text: buildAttachmentText(imageContext),
                        files: [file],
                    };
                    try {
                        if (navigator.canShare(candidate)) attachmentPayload = candidate;
                    } catch (_) {
                        // The card payload remains immediately available.
                    }
                });
            }

            return;
        }

        container.appendChild(makeButton('storybook-share-twitter', translated(t, 'share.modal.twitter', 'X / Twitter'), 'twitter', () => {
            const shareContext = { ...ctx, url: attributedUrl(ctx, 'twitter') };
            const params = new URLSearchParams({
                text: buildLeadText(shareContext),
                url: shareContext.url,
            });
            const language = ctx.language || storybookUrlApi()?.resolveLanguage?.();
            if (language) params.set('lang', language === 'zh-CN' ? 'zh-cn' : language);
            const intent = `https://x.com/intent/tweet?${params.toString()}`;
            handleIntent('twitter', intent);
        }));

        container.appendChild(makeButton('storybook-share-facebook', translated(t, 'share.modal.facebook', 'Facebook'), 'facebook', () => {
            const intent = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(attributedUrl(ctx, 'facebook'))}`;
            handleIntent('facebook', intent);
        }));

        container.appendChild(makeButton('storybook-share-reddit', translated(t, 'share.modal.reddit', 'Reddit'), 'reddit', () => {
            const intent = `https://www.reddit.com/submit?url=${encodeURIComponent(attributedUrl(ctx, 'reddit'))}&title=${encodeURIComponent(buildRedditTitle(ctx))}`;
            handleIntent('reddit', intent);
        }));

        /* Every network below takes the lead line and the attributed link in
           one message, the form its own composer unfurls into a card. Discord
           has no share address for a website at all: Copy link is its way,
           and a caller may name one Discord channel (`ctx.discord`) for a
           button that copies and opens it. */
        const withLead = (channel) => {
            const shareContext = { ...ctx, url: attributedUrl(ctx, channel) };
            return `${buildLeadText(shareContext)} ${shareContext.url}`;
        };

        container.appendChild(makeButton('storybook-share-whatsapp', translated(t, 'share.modal.whatsapp', 'WhatsApp'), 'whatsapp', () => {
            handleIntent('whatsapp', `https://wa.me/?text=${encodeURIComponent(withLead('whatsapp'))}`);
        }));

        container.appendChild(makeButton('storybook-share-telegram', translated(t, 'share.modal.telegram', 'Telegram'), 'telegram', () => {
            const shareContext = { ...ctx, url: attributedUrl(ctx, 'telegram') };
            const params = new URLSearchParams({
                url: shareContext.url,
                text: buildLeadText(shareContext),
            });
            handleIntent('telegram', `https://t.me/share/url?${params.toString()}`);
        }));

        container.appendChild(makeButton('storybook-share-bluesky', translated(t, 'share.modal.bluesky', 'Bluesky'), 'bluesky', () => {
            handleIntent('bluesky', `https://bsky.app/intent/compose?text=${encodeURIComponent(withLead('bluesky'))}`);
        }));

        container.appendChild(makeButton('storybook-share-threads', translated(t, 'share.modal.threads', 'Threads'), 'threads', () => {
            handleIntent('threads', `https://www.threads.net/intent/post?text=${encodeURIComponent(withLead('threads'))}`);
        }));

        appendDiscord();

        /*
         * NO "SAVE IMAGE". Owner-requested removal.
         *
         * It handed the reader a bare 1200x630 JPEG -- a picture of a book with
         * no link in it, which is the one thing a share of this artefact must
         * carry. Everything else in this bar sends the URL and lets the
         * destination unfurl the card from it, so the reader gets the picture
         * AND a way back. Saving the file skipped the second half.
         *
         * `SHARE_FORMATS.IMAGE_DOWNLOAD` is kept: historical rows in the
         * analytics table still carry it, and a format constant that stops
         * existing makes old data unreadable.
         */
        container.appendChild(makeButton('storybook-share-copy', translated(t, 'share.modal.copy', 'Copy link'), 'copy', async (btn) => {
            captureShare(
                'storybook_share_started',
                ctx,
                'copy',
                SHARE_FORMATS.LINK_CARD,
            );
            const ok = await copyText(attributedUrl(ctx, 'copy'));
            if (typeof ctx.onCopy === 'function') ctx.onCopy(
                ok
                    ? translated(t, 'share.modal.copyOk', 'Link copied')
                    : translated(t, 'share.modal.copyFailed', 'Could not copy share link'),
                { ok }
            );
            if (ok) {
                captureShare(
                    'storybook_share_succeeded',
                    ctx,
                    'copy',
                    SHARE_FORMATS.LINK_CARD,
                );
                if (typeof ctx.onShared === 'function') ctx.onShared('copy');
                flashOk(btn);
            }
        }));
    }

    function flashOk(btn) {
        if (!btn) return;
        btn.classList.add('storybook-share-button--ok');
        setTimeout(() => btn.classList.remove('storybook-share-button--ok'), 1100);
    }

    window.MoonfallStorybookShare = {
        attachShareButtons,
        buildRedditTitle,
        copyText,
        prepareShareImage,
        sharesThroughSheet,
    };
})();
